import React, { useCallback, useContext, useState } from 'react';
import { ButtonV2 } from '@withjoy/joykit';
import type { ButtonV2Props } from '@withjoy/joykit';
import { PeopleContext } from '@shared/context/people';
import { usePeoplePickerButtonController } from './usePeoplePickerButtonController';
import type { HardcodedLabel } from './usePeoplePickerButtonController';
import { PeopleListItem } from '@shared/components/PeopleListItem';
import { useTranslation } from '@shared/core';
import type { DeliveryMethodValue, PeoplePickerUseCase } from './PeoplePicker.types';
import { usePreloadTranslationNamespaces } from '@shared/hooks/usePreloadTranslationNamespaces';

export type PeoplePickerItemType = Readonly<'simple' | 'withDeliveryMethod'>;

export type PeoplePickerButtonProps = Readonly<
  Omit<ButtonV2Props, 'onClick' | 'children' | 'title' | 'itemType'> & {
    buttonTextDefault?: string;
    buttonTextHover?: string;
    eventHandle: string;
    title?: React.ReactNode;
    hardcodedLabels?: ReadonlyArray<HardcodedLabel>;
    onStartSelection?: () => void;
    useCase: PeoplePickerUseCase;
  } & (
      | { itemType?: 'simple'; enableSmsSelection?: never; smsOnly?: never; initialDeliveryMethod?: never }
      | { itemType: 'withDeliveryMethod'; enableSmsSelection: boolean; smsOnly?: boolean; initialDeliveryMethod?: DeliveryMethodValue }
    )
>;

export const PeoplePickerButton: React.FC<PeoplePickerButtonProps> = ({
  eventHandle,
  title: _title,
  hardcodedLabels,
  enableSmsSelection = false,
  smsOnly = false,
  initialDeliveryMethod,
  itemType = 'simple',
  onStartSelection,
  useCase,

  buttonTextDefault: _buttonTextDefault,
  buttonTextHover,

  intent = 'primary',
  variant = 'outline',
  disabled,
  ...buttonProps
}) => {
  const { tq } = useTranslation('peoplePickerButton');

  usePreloadTranslationNamespaces('peoplePicker');

  const { selectedPeople, setSelectedPeople } = useContext(PeopleContext);

  const title = _title || tq`Select Guests`;
  const buttonTextDefault = _buttonTextDefault || tq`Select Guests`;

  const [isHovering, setIsHovering] = useState(false);

  const personItemRender = useCallback(
    person => {
      if (itemType === 'withDeliveryMethod') {
        const hideEmail = person.selected && !person.hasEmailDeliveryMethodSelected && !!person.hasTextDeliveryMethodSelected;
        const hidePhone = person.selected && !person.hasTextDeliveryMethodSelected && !!person.hasEmailDeliveryMethodSelected;
        return (
          <PeopleListItem.WithDeliveryMethod
            {...person}
            enableSmsSelection={enableSmsSelection}
            shouldHideEmailIcon={hideEmail}
            shouldHidePhoneIcon={hidePhone}
          />
        );
      }

      return <PeopleListItem.Simple {...person} />;
    },
    [itemType, enableSmsSelection],
  );

  const { loading, startSelection, error } = usePeoplePickerButtonController({
    eventHandle,
    title,
    onSelectedPeople: setSelectedPeople,
    enableSmsSelection,
    smsOnly,
    initialDeliveryMethod,
    hardcodedLabels,
    personItemRender,
    useCase,
  });

  const handleClick = () => {
    onStartSelection?.();
    startSelection();
  };

  const buttonText = isHovering && selectedPeople?.length && buttonTextHover ? buttonTextHover : buttonTextDefault;

  return (
    <ButtonV2
      {...buttonProps}
      intent={intent}
      variant={variant}
      disabled={disabled || !!error}
      loading={loading}
      onClick={handleClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      shape="rounded"
    >
      {buttonText}
    </ButtonV2>
  );
};
